/**
 * Deals Analytics Calculator
 * Based on actual Cloud SQL database schema (backend/database.py)
 *
 * =====================================================================
 * DEAL FIELDS FROM DATABASE - Two Endpoints Available:
 * =====================================================================
 *
 * 1. /api/v1/deals → Uses Deal.to_dict() - Returns these fields:
 *    | Field              | Type              | Notes                     |
 *    |--------------------|-------------------|---------------------------|
 *    | id                 | String (UUID)     | Primary key               |
 *    | pitchbook_deal_id  | String            | Unique ID                 |
 *    | pitchbook_id       | String            | Alias for frontend        |
 *    | companies          | String            | Company name (NOT ID)     |
 *    | description        | String            | Company description       |
 *    | deal_synopsis      | String            | Deal synopsis             |
 *    | deal_date          | String (ISO)      | "YYYY-MM-DD"              |
 *    | deal_type          | String            | Primary type              |
 *    | deal_type_2        | String            | Secondary type            |
 *    | deal_status        | String            | Current deal status       |
 *    | deal_size_usd      | Float             | Millions USD              |
 *    | post_valuation     | Float             | Millions USD              |
 *    | primary_industry   | String            | Industry code             |
 *    | verticals          | Array<String>     | E.g., ['Gaming']          |
 *    | investors          | Array<String>     | Investor names            |
 *    | lead_investors     | Array<String>     | Lead investors            |
 *    | financing_status   | String            | Financing status          |
 *    | business_status    | String            | Business status           |
 *    | hq_location        | String            | Full location string      |
 *
 * 2. /api/v1/deals/full → Raw SQL returns ALL 21+ columns including:
 *    | Additional Fields         | Notes                           |
 *    |---------------------------|---------------------------------|
 *    | hq_country                | Country name                    |
 *    | hq_global_region          | Region name                     |
 *    | current_business_status   | Full business status            |
 *    | primary_industry_code     | Raw industry code               |
 *    | + many more...            |                                 |
 *
 * NOTE: Analytics may receive data from either endpoint depending on
 * how the frontend fetches data. Field name fallbacks are implemented.
 * =====================================================================
 */

import {
  countBy,
  getTopN,
  getMostCommon,
  groupByMonth,
  getDateRange,
  calculatePercentage
} from './chartHelpers';

import {
  removeOutliers,
  applyLogScale,
  calculateLinearRegression,
  normalizeBubbleSizes,
  generateTransformationInfo
} from './dataTransformations';

/**
 * Generate detailed field info for chart tooltips/popups
 * Explains what data is being used and potential issues
 */
const generateFieldInfo = (fieldName, dataType, description, sampleCount, totalCount, issues = []) => {
  return {
    fieldName,
    dataType,
    description,
    coverage: totalCount > 0 ? `${sampleCount}/${totalCount} (${((sampleCount/totalCount)*100).toFixed(1)}%)` : '0%',
    issues: issues.length > 0 ? issues : null
  };
};

/**
 * Helper to get field value with fallbacks for different API response formats
 * @param {Object} deal - Deal object
 * @param {Array<String>} fieldNames - Array of possible field names to try
 * @returns {*} First non-null/undefined value found
 */
const getFieldValue = (deal, fieldNames) => {
  for (const field of fieldNames) {
    if (deal[field] !== null && deal[field] !== undefined && deal[field] !== '') {
      return deal[field];
    }
  }
  return null;
};

/**
 * Helper to count by field with fallbacks
 */
const countByWithFallback = (array, fieldNames) => {
  const counts = {};
  array.forEach(item => {
    const value = getFieldValue(item, fieldNames);
    if (value !== null && value !== undefined && value !== '') {
      counts[value] = (counts[value] || 0) + 1;
    }
  });
  return counts;
};

export const calculateDealsAnalytics = (deals, allDeals, options = {}) => {
  if (!deals || deals.length === 0) {
    return {
      summary: { total: 0, cards: [] },
      charts: []
    };
  }

  const { expanded = false } = options;

  const total = deals.length;
  const dateRange = getDateRange(deals, 'deal_date');

  // Use fallbacks for fields that may have different names
  // primary_industry (from to_dict) vs primary_industry_code (from raw SQL)
  const topIndustry = getMostCommon(deals, 'primary_industry') || getMostCommon(deals, 'primary_industry_code');

  // hq_global_region only in /full endpoint, hq_location in basic endpoint
  const topRegion = getMostCommon(deals, 'hq_global_region') || getMostCommon(deals, 'hq_location');

  const topDealType = getMostCommon(deals, 'deal_type');

  // Summary cards
  const summaryCards = [
    {
      label: 'Total Deals',
      value: total.toLocaleString()
    },
    {
      label: 'Date Range',
      value: dateRange ? dateRange.span : 'N/A'
    },
    {
      label: 'Top Industry',
      value: topIndustry ? truncateIndustry(topIndustry.value) : 'N/A'
    },
    {
      label: 'Top Region',
      value: topRegion ? topRegion.value : 'N/A'
    }
  ];

  // ============================================================
  // Chart 1: Deal Flow Timeline (Line Chart)
  // ============================================================
  const dealsWithDate = deals.filter(d => d.deal_date);
  const timelineData = groupByMonth(deals, 'deal_date');
  const timelineTransformInfo = {
    chartTitle: 'Deal Flow Over Time',
    interpretation: 'Shows the volume of deals closed per month. Useful for identifying market cycles, seasonal patterns, and investment trends.',
    procedures: [
      `• Field: deal_date (Date/ISO String)`,
      `• Data Type: Date parsed from ISO string`,
      `• Coverage: ${dealsWithDate.length}/${total} deals have valid dates (${((dealsWithDate.length/total)*100).toFixed(1)}%)`,
      `• Transformation: Grouped by YYYY-MM, sorted chronologically`,
      `• Output: Monthly deal counts`
    ],
    sampleSize: `${dealsWithDate.length} deals with valid dates`
  };

  // ============================================================
  // Chart 2: Top 10 Deal Types (Horizontal Bar) - FIXED
  // ============================================================
  const dealsWithType = deals.filter(d => d.deal_type && typeof d.deal_type === 'string' && d.deal_type.trim() !== '');
  const dealTypeCounts = countBy(dealsWithType, 'deal_type');

  // Debug: Log what we're getting
  console.log('[DealsAnalytics] Deal type counts:', dealTypeCounts);
  console.log('[DealsAnalytics] Sample deal_type values:', deals.slice(0, 5).map(d => ({ deal_type: d.deal_type, type: typeof d.deal_type })));

  // Ensure we have proper string labels, not numbers
  const topDealTypes = Object.entries(dealTypeCounts)
    .filter(([name, value]) => name && name !== 'undefined' && name !== 'null' && isNaN(Number(name)))
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, value]) => ({
      name: String(name).trim() || 'Unknown Type',
      value: Number(value)
    }));

  const dealTypesTransformInfo = {
    chartTitle: 'Top 10 Deal Types',
    interpretation: 'Distribution of deals by their primary classification (Series A, B, C, M&A, IPO, etc.). Helps understand the funding stage mix in your dataset.',
    procedures: [
      `• Field: deal_type (String)`,
      `• Data Type: Categorical string`,
      `• Coverage: ${dealsWithType.length}/${total} deals have deal types (${((dealsWithType.length/total)*100).toFixed(1)}%)`,
      `• Unique Types Found: ${Object.keys(dealTypeCounts).length}`,
      `• Transformation: Count by category, top 10 shown`,
      `• Note: Empty/null values excluded`
    ],
    sampleSize: `${dealsWithType.length} deals with valid deal types`,
    issues: dealsWithType.length < total * 0.5 ? ['⚠️ Less than 50% of deals have deal_type data'] : null
  };

  // ============================================================
  // Chart 3: Industry Distribution (Pie Chart) - WITH "OTHERS" GROUP
  // ============================================================
  // Supports both field names: primary_industry (to_dict) and primary_industry_code (raw SQL)
  const dealsWithIndustry = deals.filter(d => d.primary_industry || d.primary_industry_code);
  const industryCounts = countByWithFallback(deals, ['primary_industry', 'primary_industry_code']);
  const allIndustries = Object.entries(industryCounts)
    .filter(([name, _]) => name && name !== 'undefined' && name !== 'null')
    .sort((a, b) => b[1] - a[1]);

  // Take top 5 and group rest as "Others"
  const topIndustriesRaw = allIndustries.slice(0, 5);
  const othersCount = allIndustries.slice(5).reduce((sum, [_, count]) => sum + count, 0);

  const topIndustries = topIndustriesRaw.map(([name, value]) => ({ name, value }));
  if (othersCount > 0) {
    topIndustries.push({ name: 'Others', value: othersCount });
  }

  const industryFieldUsed = deals.some(d => d.primary_industry) ? 'primary_industry' : 'primary_industry_code';
  const industriesTransformInfo = {
    chartTitle: 'Top Industries',
    interpretation: 'Shows industry concentration of deals. "Others" combines smaller industry categories for cleaner visualization.',
    procedures: [
      `• Field: ${industryFieldUsed} (String)`,
      `• Data Type: Categorical string`,
      `• Coverage: ${dealsWithIndustry.length}/${total} deals (${((dealsWithIndustry.length/total)*100).toFixed(1)}%)`,
      `• Total Industries: ${allIndustries.length}`,
      `• Transformation: Top 5 shown individually, remaining ${Math.max(0, allIndustries.length - 5)} grouped as "Others"`,
      `• "Others" contains: ${othersCount} deals (${((othersCount/total)*100).toFixed(1)}%)`
    ],
    sampleSize: `${dealsWithIndustry.length} deals with industry classification`
  };

  // ============================================================
  // Chart 4: Geographic Distribution (Horizontal Bar or Heatmap)
  // ============================================================
  // hq_country and hq_global_region only available from /api/v1/deals/full
  // hq_location available from basic /api/v1/deals endpoint
  const dealsWithCountry = deals.filter(d => d.hq_country || d.hq_global_region || d.hq_location);

  // Try country first (most specific), fall back to region, then location
  const countryCounts = countByWithFallback(deals, ['hq_country']);
  const regionCounts = countByWithFallback(deals, ['hq_global_region', 'hq_location']);

  // Determine which geo field is populated
  const hasCountryData = Object.keys(countryCounts).length > 0;
  const hasRegionData = Object.keys(regionCounts).length > 0;

  // Create heatmap-ready data with country codes (if country data available)
  const countryData = Object.entries(countryCounts)
    .filter(([country, _]) => country && country !== 'undefined' && country !== 'null')
    .sort((a, b) => b[1] - a[1])
    .map(([country, count]) => ({
      name: country,
      value: count,
      code: getCountryCode(country)
    }));

  // Region/location data for horizontal bar fallback
  const regionData = Object.entries(regionCounts)
    .filter(([region, _]) => region && region !== 'undefined' && region !== 'null')
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value }));

  const geoFieldUsed = hasCountryData ? 'hq_country' : (hasRegionData ? 'hq_global_region/hq_location' : 'none');
  const geoTransformInfo = {
    chartTitle: 'Geographic Distribution',
    interpretation: 'Shows where deals are headquartered. Useful for identifying geographic concentration and market opportunities.',
    procedures: [
      `• Primary Field: ${geoFieldUsed}`,
      `• Data Type: Categorical string`,
      `• Coverage: ${dealsWithCountry.length}/${total} deals have location data (${((dealsWithCountry.length/total)*100).toFixed(1)}%)`,
      hasCountryData ? `• Countries Found: ${Object.keys(countryCounts).length}` : '• Countries: Not available (use /api/v1/deals/full)',
      hasRegionData ? `• Regions Found: ${Object.keys(regionCounts).length}` : '• Regions: Not available',
      `• Transformation: Aggregated count by geographic area`
    ],
    sampleSize: `${dealsWithCountry.length} deals with geographic data`,
    issues: !hasCountryData && !hasRegionData ? ['⚠️ No geographic data available. Try /api/v1/deals/full endpoint.'] : null
  };

  // ============================================================
  // Chart 5: Business Status (Pie Chart) - Kept, Deal Status Removed
  // ============================================================
  // business_status from to_dict(), current_business_status from raw SQL
  const dealsWithBusinessStatus = deals.filter(d => d.business_status || d.current_business_status);
  const businessStatusCounts = countByWithFallback(deals, ['business_status', 'current_business_status']);
  const allBusinessStatuses = Object.entries(businessStatusCounts)
    .filter(([name, _]) => name && name !== 'undefined' && name !== 'null')
    .sort((a, b) => b[1] - a[1]);

  // Take top 5 and group rest as "Others"
  const topBusinessStatusesRaw = allBusinessStatuses.slice(0, 5);
  const businessOthersCount = allBusinessStatuses.slice(5).reduce((sum, [_, count]) => sum + count, 0);

  const topBusinessStatuses = topBusinessStatusesRaw.map(([name, value]) => ({ name, value }));
  if (businessOthersCount > 0) {
    topBusinessStatuses.push({ name: 'Others', value: businessOthersCount });
  }

  const businessFieldUsed = deals.some(d => d.business_status) ? 'business_status' : 'current_business_status';
  const businessStatusTransformInfo = {
    chartTitle: 'Business Status',
    interpretation: 'Current operational status of companies in these deals (Operating, Acquired, Out of Business, etc.).',
    procedures: [
      `• Field: ${businessFieldUsed} (String)`,
      `• Data Type: Categorical string`,
      `• Coverage: ${dealsWithBusinessStatus.length}/${total} deals (${((dealsWithBusinessStatus.length/total)*100).toFixed(1)}%)`,
      `• Unique Statuses: ${allBusinessStatuses.length}`,
      `• Transformation: Top 5 shown, rest grouped as "Others"`
    ],
    sampleSize: `${dealsWithBusinessStatus.length} deals with business status`,
    issues: dealsWithBusinessStatus.length === 0 ? ['⚠️ No business status data available'] : null
  };

  // ============================================================
  // Chart 6: Deal Size vs Valuation (Scatter Plot)
  // ============================================================
  let scatterDataRaw = deals
    .filter(d => d.deal_size_usd && d.post_valuation && d.deal_date && !isNaN(d.deal_size_usd) && !isNaN(d.post_valuation))
    .map(d => ({
      name: truncateText(d.companies || 'Unknown', 25),
      dealSize: parseFloat(d.deal_size_usd),
      valuation: parseFloat(d.post_valuation),
      dealDate: d.deal_date,
      x: parseFloat(d.deal_size_usd),
      y: parseFloat(d.post_valuation),
      bubbleSize: d.deal_date ? new Date(d.deal_date).getFullYear() : 2020
    }))
    .filter(d => d.x > 0 && d.y > 0)
    .slice(0, 150);

  // Remove extreme outliers
  const { cleaned: scatterCleaned, stats: scatterOutlierStats } = removeOutliers(
    scatterDataRaw,
    'y',
    2.5
  );

  // Apply log scale
  const scatterLog = applyLogScale(scatterCleaned, 'x', 'y');

  // Normalize bubble sizes
  const scatterData = normalizeBubbleSizes(scatterLog, 'bubbleSize', 30, 100);

  // Calculate regression
  const scatterRegression = calculateLinearRegression(scatterLog, 'x', 'y');

  const scatterTransformInfo = {
    chartTitle: 'Deal Size vs Valuation',
    interpretation: 'Relationship between deal investment size and company valuation. Points above the trend line got higher valuations relative to deal size.',
    procedures: [
      `• X-Axis: deal_size_usd (Float, millions USD)`,
      `• Y-Axis: post_valuation (Float, millions USD)`,
      `• Bubble Size: deal_date year (newer = larger)`,
      `• Coverage: ${scatterDataRaw.length}/${total} deals have both fields (${((scatterDataRaw.length/total)*100).toFixed(1)}%)`,
      `• Outlier Removal: IQR method (2.5×), ${scatterOutlierStats?.outlierCount || 0} removed`,
      `• Scale: Log₁₀ applied (values span multiple orders of magnitude)`,
      scatterRegression ? `• Regression: R² = ${scatterRegression.r2.toFixed(3)}` : '• Regression: Insufficient data'
    ],
    sampleSize: `${scatterData.length} deals after cleaning`
  };

  // ============================================================
  // Build Charts Array
  // ============================================================
  const charts = [
    {
      id: 'timeline',
      title: 'Deal Flow Over Time',
      type: 'line',
      data: timelineData,
      note: timelineData.length > 0 ? `${timelineData.length} months tracked` : 'No date data available',
      transformInfo: timelineTransformInfo
    },
    {
      id: 'deal_types',
      title: 'Top 10 Deal Types',
      type: 'horizontal-bar',
      data: topDealTypes,
      note: topDealTypes.length > 0
        ? `${topDealTypes[0]?.name || 'Unknown'} leads (${topDealTypes[0] ? calculatePercentage(topDealTypes[0].value, total) : 0}%)`
        : '⚠️ No valid deal types found - check data',
      transformInfo: dealTypesTransformInfo
    },
    {
      id: 'industries',
      title: 'Top Industries',
      type: 'pie',
      data: topIndustries,
      note: `${topIndustries.length - (othersCount > 0 ? 1 : 0)} industries + Others (${allIndustries.length} total)`,
      transformInfo: industriesTransformInfo
    },
    {
      id: 'geo_heatmap',
      title: 'Geographic Distribution',
      type: 'geo-heatmap',
      data: countryData,
      fallbackData: regionData, // Use this if heatmap can't render
      note: `${countryData.length} countries • ${dealsWithCountry.length} deals with location`,
      transformInfo: geoTransformInfo
    },
    {
      id: 'business_status',
      title: 'Business Status',
      type: 'pie',
      data: topBusinessStatuses,
      note: `${topBusinessStatuses.length - (businessOthersCount > 0 ? 1 : 0)} statuses + Others`,
      transformInfo: businessStatusTransformInfo
    },
    {
      id: 'scatter_deal_size_valuation',
      title: 'Deal Size vs Valuation',
      type: 'scatter',
      data: scatterData,
      xLabel: 'Deal Size ($M, log₁₀)',
      yLabel: 'Post-Money Valuation ($M, log₁₀)',
      note: `${scatterData.length} deals • log scale • ${scatterOutlierStats?.outlierCount || 0} outliers removed`,
      useLogScale: true,
      regression: scatterRegression,
      transformInfo: scatterTransformInfo
    }
  ];

  // ============================================================
  // EXPANDED MODE: Additional detailed charts
  // ============================================================
  if (expanded) {
    // Chart 7: Valuation Distribution (Histogram)
    const dealsWithValuation = deals.filter(d => d.post_valuation && !isNaN(parseFloat(d.post_valuation)));
    const valuationRanges = [
      { label: '<$10M', min: 0, max: 10 },
      { label: '$10M-$50M', min: 10, max: 50 },
      { label: '$50M-$100M', min: 50, max: 100 },
      { label: '$100M-$500M', min: 100, max: 500 },
      { label: '$500M-$1B', min: 500, max: 1000 },
      { label: '$1B+', min: 1000, max: 1000000 }
    ];

    const valuationData = valuationRanges.map(range => ({
      name: range.label,
      value: dealsWithValuation.filter(d => {
        const val = parseFloat(d.post_valuation);
        return val >= range.min && val < range.max;
      }).length
    })).filter(d => d.value > 0);

    const valuationTransformInfo = {
      chartTitle: 'Valuation Distribution',
      interpretation: 'Distribution of deal valuations across different size buckets. Helps understand the valuation landscape.',
      procedures: [
        `• Field: post_valuation (Float, millions USD)`,
        `• Data Type: Numeric float`,
        `• Coverage: ${dealsWithValuation.length}/${total} deals (${((dealsWithValuation.length/total)*100).toFixed(1)}%)`,
        `• Transformation: Bucketed into 6 valuation ranges`,
        `• Note: Values in millions USD`
      ],
      sampleSize: `${dealsWithValuation.length} deals with valuation data`
    };

    if (valuationData.length > 0) {
      charts.push({
        id: 'valuation_distribution',
        title: 'Valuation Distribution',
        type: 'bar',
        data: valuationData,
        note: `${valuationData.reduce((sum, d) => sum + d.value, 0)} deals with valuation data`,
        transformInfo: valuationTransformInfo
      });
    }

    // Chart 8: Deal Size Distribution (Histogram)
    const dealsWithSize = deals.filter(d => d.deal_size_usd && !isNaN(parseFloat(d.deal_size_usd)));
    const dealSizeRanges = [
      { label: '<$1M', min: 0, max: 1 },
      { label: '$1M-$5M', min: 1, max: 5 },
      { label: '$5M-$10M', min: 5, max: 10 },
      { label: '$10M-$50M', min: 10, max: 50 },
      { label: '$50M-$100M', min: 50, max: 100 },
      { label: '$100M+', min: 100, max: 1000000 }
    ];

    const dealSizeData = dealSizeRanges.map(range => ({
      name: range.label,
      value: dealsWithSize.filter(d => {
        const size = parseFloat(d.deal_size_usd);
        return size >= range.min && size < range.max;
      }).length
    })).filter(d => d.value > 0);

    const dealSizeTransformInfo = {
      chartTitle: 'Deal Size Distribution',
      interpretation: 'Distribution of deal investment sizes. Shows typical check sizes in your dataset.',
      procedures: [
        `• Field: deal_size_usd (Float, millions USD)`,
        `• Data Type: Numeric float`,
        `• Coverage: ${dealsWithSize.length}/${total} deals (${((dealsWithSize.length/total)*100).toFixed(1)}%)`,
        `• Transformation: Bucketed into 6 size ranges`,
        `• Note: Values in millions USD`
      ],
      sampleSize: `${dealsWithSize.length} deals with size data`
    };

    if (dealSizeData.length > 0) {
      charts.push({
        id: 'deal_size_distribution',
        title: 'Deal Size Distribution',
        type: 'bar',
        data: dealSizeData,
        note: `${dealSizeData.reduce((sum, d) => sum + d.value, 0)} deals with size data`,
        transformInfo: dealSizeTransformInfo
      });
    }

    // Chart 9: Deal Type 2 - Secondary Classification (FIXED - Proper horizontal bar)
    const dealsWithType2 = deals.filter(d => d.deal_type_2 && typeof d.deal_type_2 === 'string' && d.deal_type_2.trim() !== '');

    if (dealsWithType2.length > 0) {
      const dealType2Counts = countBy(dealsWithType2, 'deal_type_2');

      // Ensure proper string labels
      const topDealTypes2 = Object.entries(dealType2Counts)
        .filter(([name, _]) => name && name !== 'undefined' && name !== 'null' && isNaN(Number(name)))
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name, value]) => ({
          name: String(name).trim() || 'Unknown',
          value: Number(value)
        }));

      const dealType2TransformInfo = {
        chartTitle: 'Secondary Deal Classification',
        interpretation: 'Secondary categorization of deals (e.g., lead round, follow-on, bridge, etc.). Provides additional context beyond primary deal type.',
        procedures: [
          `• Field: deal_type_2 (String)`,
          `• Data Type: Categorical string`,
          `• Coverage: ${dealsWithType2.length}/${total} deals (${((dealsWithType2.length/total)*100).toFixed(1)}%)`,
          `• Unique Types: ${Object.keys(dealType2Counts).length}`,
          `• Transformation: Count by category, top 10 shown`
        ],
        sampleSize: `${dealsWithType2.length} deals with secondary type`,
        issues: dealsWithType2.length < total * 0.3 ? ['⚠️ Less than 30% of deals have deal_type_2 data'] : null
      };

      if (topDealTypes2.length > 0) {
        charts.push({
          id: 'deal_types_2',
          title: 'Secondary Deal Classification',
          type: 'horizontal-bar',
          data: topDealTypes2,
          note: topDealTypes2.length > 0
            ? `${topDealTypes2[0]?.name || 'Unknown'} leads • ${Object.keys(dealType2Counts).length} unique types`
            : '⚠️ No valid secondary types found',
          transformInfo: dealType2TransformInfo
        });
      }
    }

    // Chart 10: Year-over-Year Deal Volume Trend
    const dealsWithYear = deals.filter(d => d.deal_date);
    if (dealsWithYear.length > 0) {
      const yearCounts = {};
      dealsWithYear.forEach(d => {
        try {
          const year = new Date(d.deal_date).getFullYear();
          if (!isNaN(year) && year > 1990 && year < 2100) {
            yearCounts[year] = (yearCounts[year] || 0) + 1;
          }
        } catch (e) { /* skip invalid dates */ }
      });

      const yearData = Object.entries(yearCounts)
        .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
        .map(([year, count]) => ({ name: year, value: count }));

      const yoyTransformInfo = {
        chartTitle: 'Annual Deal Volume',
        interpretation: 'Year-over-year deal activity. Shows growth or decline trends across years.',
        procedures: [
          `• Field: deal_date (Date/ISO String)`,
          `• Data Type: Date extracted to year`,
          `• Coverage: ${dealsWithYear.length}/${total} deals (${((dealsWithYear.length/total)*100).toFixed(1)}%)`,
          `• Years Covered: ${Object.keys(yearCounts).length}`,
          `• Transformation: Grouped by calendar year`
        ],
        sampleSize: `${dealsWithYear.length} deals with valid dates`
      };

      if (yearData.length > 1) {
        charts.push({
          id: 'yoy_volume',
          title: 'Annual Deal Volume',
          type: 'bar',
          data: yearData,
          note: `${yearData.length} years • ${dealsWithYear.length} deals`,
          transformInfo: yoyTransformInfo
        });
      }
    }
  }

  return {
    summary: {
      total,
      filtered: allDeals && total !== allDeals.length ? total : null,
      cards: summaryCards
    },
    charts: charts.filter(chart => chart.data && chart.data.length > 0)
  };
};

// Helper to truncate long industry names
const truncateIndustry = (industry) => {
  if (!industry) return '';
  if (industry.length <= 20) return industry;
  return industry.substring(0, 17) + '...';
};

// Helper to truncate text
const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Map country names to ISO 3166-1 alpha-2 codes for geographic visualizations
 * This is a subset of common countries - extend as needed
 */
const getCountryCode = (countryName) => {
  if (!countryName) return null;

  const countryMap = {
    // North America
    'United States': 'US', 'United States of America': 'US', 'USA': 'US', 'US': 'US',
    'Canada': 'CA',
    'Mexico': 'MX',

    // Europe
    'United Kingdom': 'GB', 'UK': 'GB', 'Great Britain': 'GB', 'England': 'GB',
    'Germany': 'DE', 'Deutschland': 'DE',
    'France': 'FR',
    'Italy': 'IT',
    'Spain': 'ES',
    'Netherlands': 'NL', 'Holland': 'NL',
    'Switzerland': 'CH',
    'Sweden': 'SE',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Finland': 'FI',
    'Ireland': 'IE',
    'Belgium': 'BE',
    'Austria': 'AT',
    'Poland': 'PL',
    'Portugal': 'PT',
    'Czech Republic': 'CZ', 'Czechia': 'CZ',
    'Greece': 'GR',
    'Luxembourg': 'LU',

    // Asia Pacific
    'China': 'CN', 'People\'s Republic of China': 'CN',
    'Japan': 'JP',
    'South Korea': 'KR', 'Korea': 'KR', 'Republic of Korea': 'KR',
    'India': 'IN',
    'Singapore': 'SG',
    'Hong Kong': 'HK',
    'Taiwan': 'TW',
    'Australia': 'AU',
    'New Zealand': 'NZ',
    'Indonesia': 'ID',
    'Malaysia': 'MY',
    'Thailand': 'TH',
    'Vietnam': 'VN',
    'Philippines': 'PH',

    // Middle East
    'Israel': 'IL',
    'United Arab Emirates': 'AE', 'UAE': 'AE',
    'Saudi Arabia': 'SA',

    // Latin America
    'Brazil': 'BR',
    'Argentina': 'AR',
    'Chile': 'CL',
    'Colombia': 'CO',

    // Africa
    'South Africa': 'ZA',
    'Nigeria': 'NG',
    'Kenya': 'KE',
    'Egypt': 'EG'
  };

  // Direct match
  if (countryMap[countryName]) {
    return countryMap[countryName];
  }

  // Case-insensitive match
  const lowerName = countryName.toLowerCase();
  for (const [name, code] of Object.entries(countryMap)) {
    if (name.toLowerCase() === lowerName) {
      return code;
    }
  }

  // Partial match (for variations like "United States of America, California")
  for (const [name, code] of Object.entries(countryMap)) {
    if (lowerName.includes(name.toLowerCase()) || name.toLowerCase().includes(lowerName)) {
      return code;
    }
  }

  return null; // Unknown country
};
